import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

const IMAGE_BUCKET = 'portal-assets';
const COLOR_PRESET = ['#111827', '#1d4ed8', '#047857', '#b91c1c', '#f59e0b'];
const HIGHLIGHT_PRESET = ['#fde68a', '#bfdbfe', '#bbf7d0'];

const MenuButton = ({ active, disabled, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`px-2.5 py-1 text-xs sm:text-sm font-medium rounded border border-gray-200 transition-colors ${
      active ? 'bg-gray-300' : 'bg-white hover:bg-gray-100'
    } disabled:opacity-40`}
  >
    {children}
  </button>
);

const ColorSwatch = ({ color, onSelect, active, title }) => (
  <button
    type="button"
    onClick={onSelect}
    title={title}
    className={`w-6 h-6 rounded-full border-2 ${
      active ? 'border-blue-500 scale-110' : 'border-transparent'
    } transition-transform`}
    style={{ backgroundColor: color }}
  />
);

const MenuBar = ({ editor, onImageRequest, isUploading }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', previousUrl);
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url.trim(), target: '_blank', rel: 'noopener noreferrer' })
      .run();
  };

  const clearFormatting = () => {
    editor.chain().focus().unsetAllMarks().clearNodes().run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold"
      >
        B
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic"
      >
        I
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editor.can().chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="Underline"
      >
        U
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
        title="Strikethrough"
      >
        S
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <MenuButton
        onClick={() => setLink()}
        active={editor.isActive('link')}
        title="Add link"
      >
        Link
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive('link')}
        title="Remove link"
      >
        Unlink
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet list"
      >
        • List
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered list"
      >
        1. List
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')}
        title="Quote"
      >
        ❝
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        active={editor.isActive('codeBlock')}
        title="Code block"
      >
        {'</>'}
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
        title="Align left"
      >
        ⬅
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
        title="Align center"
      >
        ⬍
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })}
        title="Align right"
      >
        ➡
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().unsetTextAlign().run()}
        title="Reset alignment"
      >
        ↔
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {COLOR_PRESET.map((color) => (
        <ColorSwatch
          key={color}
          color={color}
          onSelect={() => editor.chain().focus().setColor(color).run()}
          active={editor.isActive('textStyle', { color })}
          title={`Text color ${color}`}
        />
      ))}
      <MenuButton
        onClick={() => editor.chain().focus().unsetColor().run()}
        title="Clear text color"
      >
        Clear
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {HIGHLIGHT_PRESET.map((color) => (
        <ColorSwatch
          key={`highlight-${color}`}
          color={color}
          onSelect={() => editor.chain().focus().toggleHighlight({ color }).run()}
          active={editor.isActive('highlight', { color })}
          title={`Highlight ${color}`}
        />
      ))}
      <MenuButton
        onClick={() => editor.chain().focus().unsetHighlight().run()}
        title="Clear highlight"
      >
        ▲
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <MenuButton
        onClick={onImageRequest}
        disabled={isUploading}
        title="Insert image"
      >
        {isUploading ? 'Uploading…' : 'Image'}
      </MenuButton>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <MenuButton onClick={clearFormatting} title="Clear formatting">
        Reset
      </MenuButton>
    </div>
  );
};

/**
 * RichTextEditor component using Tiptap.
 * Provides extended formatting plus image uploads to Supabase storage.
 */
export default function RichTextEditor({
  content,
  onChange,
  placeholder = 'Start typing...',
  className = '',
  teamId
}) {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4]
        }
      }),
      Underline,
      Link.configure({
        autolink: true,
        openOnClick: true,
        validate: (href) => /^https?:\/\//i.test(href)
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg my-2'
        }
      })
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-3'
      }
    }
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const handleImageRequest = () => {
    fileInputRef.current?.click();
  };

  const handleImageSelection = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!editor) return;

    if (!teamId) {
      toast.error('Team information missing. Refresh and try again.');
      event.target.value = '';
      return;
    }

    const fileExt = file.name.split('.').pop() || 'png';
    const storagePath = `${teamId}/portal/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

    setIsUploading(true);
    try {
      const { error } = await supabase.storage
        .from(IMAGE_BUCKET)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      const { data } = supabase.storage.from(IMAGE_BUCKET).getPublicUrl(storagePath);
      const publicUrl = data?.publicUrl;

      if (!publicUrl) {
        throw new Error('Unable to generate public URL for uploaded image.');
      }

      editor.chain().focus().setImage({ src: publicUrl, alt: file.name }).run();
      toast.success('Image added to content');
    } catch (error) {
      console.error('Image upload failed:', error);
      const message = error?.message?.includes('Storage bucket not found')
        ? 'Portal assets bucket not found. Please create a "portal-assets" bucket in Supabase Storage and mark it public.'
        : error.message || 'Failed to upload image.';
      toast.error(message);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden bg-white ${className}`}>
      <MenuBar editor={editor} onImageRequest={handleImageRequest} isUploading={isUploading} />
      <EditorContent editor={editor} placeholder={placeholder} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelection}
      />
    </div>
  );
}
